import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { v4 as uuidv4 } from 'uuid'; // For generating unique IDs for saved configurations
import { saveAs } from 'file-saver'; // For exporting configuration files
import CustomToggle from '../CustomToggle'; // Import CustomToggle component

// Import all non-voice audio files with corrected paths
import gongAudio from '../../assets/audios/breathing/gong/gong.mp3';
import pingAudio from '../../assets/audios/breathing/gong/ping.mp3';
import ainsaMusic from '../../assets/audios/breathing/music/ainsa.mp3';
import blissMusic from '../../assets/audios/breathing/music/bliss.mp3';
import soundExhalingAudio from '../../assets/audios/breathing/sound/exhaling.mp3';
import soundInhalingAudio from '../../assets/audios/breathing/sound/inhaling.mp3';

// Import all SPANISH voice audio files
import voiceExhaleAudioEs from '../../assets/audios/breathing/voice/es/exhale.mp3';
import voiceInhaleAudioEs from '../../assets/audios/breathing/voice/es/inhale.mp3';
import voiceRetentionAudioEs from '../../assets/audios/breathing/voice/es/retention.mp3';
import voiceRoundAudioEs from '../../assets/audios/breathing/voice/es/round.mp3';
import voice15SegAudioEs from '../../assets/audios/breathing/voice/es/15seg.mp3';
import inhaleProfoundEs from '../../assets/audios/breathing/voice/es/profound.mp3';
import voicePrepareAudioEs from '../../assets/audios/breathing/voice/es/prepare.mp3';

// Import all ENGLISH voice audio files (assuming they exist in the /en/ folder with the same names)
import voiceExhaleAudioEn from '../../assets/audios/breathing/voice/en/exhale.mp3';
import voiceInhaleAudioEn from '../../assets/audios/breathing/voice/en/inhale.mp3';
import voiceRetentionAudioEn from '../../assets/audios/breathing/voice/en/retention.mp3';
import voiceRoundAudioEn from '../../assets/audios/breathing/voice/en/round.mp3';
import voice15SegAudioEn from '../../assets/audios/breathing/voice/en/15seg.mp3';
import inhaleProfoundEn from '../../assets/audios/breathing/voice/en/profound.mp3';
import voicePrepareAudioEn from '../../assets/audios/breathing/voice/en/prepare.mp3';

import UrlConfig from '../../data/UrlConfig';
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';
import { useLocation } from 'react-router-dom';
import CommentSection from '../comments/CommentSection';
import ContactSection from '../common/ContactSection';

// Define translations for different languages
const translations = {
  es: {
    wimHofTitle: 'Respiración Wim Hof',
    wimHofDescription: 'Personaliza y experimenta la poderosa técnica de respiración Wim Hof.',
    audioVolumesTitle: 'Volumen de Audios',
    backgroundMusicLabel: 'Música de Fondo',
    gongPingLabel: 'Gong y Ping',
    breathingSoundsLabel: 'Sonidos de Respiración',
    voiceCuesLabel: 'Voces Guía',
    globalSettingsTitle: 'Ajustes Globales',
    selectMusicLabel: 'Seleccionar Música',
    ainsaMusic: 'Ainsa',
    blissMusic: 'Bliss',
    inhalationExhalationSoundLabel: 'Sonido de Inhalación/Exhalación',
    initialWaitTimeLabel: 'Tiempo de Espera Inicial (seg, 0 para omitir)',
    numberOfRoundsLabel: 'Cantidad de Rondas',
    generalSettingsTitle: 'Configuración General',
    roundTitle: (index) => `Ronda ${index + 1}`,
    deleteRoundButton: 'Eliminar Ronda',
    inhalationSpeedLabel: 'Velocidad Inhalación (seg, mín. 0.5)',
    exhalationSpeedLabel: 'Velocidad Exhalación (seg, mín. 0.5)',
    breathingPeriodDurationLabel: 'Duración del Período de Respiración',
    numberOfBreathsOption: 'Cantidad de Respiraciones',
    totalBreathingTimeOption: 'Tiempo Total de Respiración',
    numberOfBreathsInputLabel: 'Cantidad de Respiraciones (mín. 33)',
    totalBreathingTimeInputLabel: 'Tiempo Total de Respiración (seg, mín. 55)',
    retentionTimeLabel: 'Tiempo de Apnea (seg, mín. 55)',
    voiceCuesFirstBreathsLabel: 'Voces Inhale/Exhale en primeras X respiraciones (0 para siempre)',
    inhaleHoldTimeLabel: 'Tiempo de Inhalar y Mantener',
    roundSettingsTitle: 'Ajustes por Ronda',
    addRoundButton: '+ Agregar Ronda',
    configManagementTitle: 'Gestión de Configuraciones',
    configNamePlaceholder: 'Nombre para guardar config.',
    saveButton: 'Guardar',
    loadConfigLabel: 'Cargar Configuración Guardada',
    selectOption: 'Seleccionar...',
    deleteButtonTitle: 'Eliminar',
    exportButtonTitle: 'Exportar',
    importConfigLabel: 'Importar Configuración desde Archivo',
    configImportSuccess: (name) => `Configuración "${name}" importada exitosamente.`,
    invalidConfigFile: 'El archivo importado no es una configuración válida.',
    errorParsingConfigFile: 'Error al leer o parsear el archivo de configuración.',
    startBreathingButton: 'Comenzar Respiración',
    initialPreparationPhase: (time) => `Preparación Inicial... ${time}`,
    roundPreparationPhase: (time) => `Preparación de Ronda... ${time}`,
    inhalePhase: 'INHAAALA',
    exhalePhase: 'EXHALA',
    breathingTimeRemaining: (time) => `Tiempo restante de respiración: ${time}`,
    retentionPhase: (time) => `RETENCIÓN: ${time}`,
    inhaleHoldPhase: (time) => `Inhala y Retén: ${time}`,
    recoveryPhase: (time) => `Recuperación... ${time}`,
    totalTime: (time) => `Tiempo total: ${time}`,
    stopExerciseButton: 'Detener Ejercicio',
    exerciseCompletedTitle: '¡Ejercicio Completado! 🎉',
    roundsCompleted: 'Rondas Completadas:',
    totalBreathsApprox: 'Respiraciones Totales (aprox.):',
    averageRetentionTime: 'Tiempo Promedio de Retención:',
    totalExerciseTime: 'Tiempo Total del Ejercicio:',
    retentionTimesPerRound: 'Tiempos de Retención por Ronda:',
    roundTime: (index, time) => `Ronda ${index + 1}: ${time}`,
    backToConfigButton: 'Volver a Configuración',
    footerText: (year) => `© ${year} Gus • Aguiar | En misión de elevar y transformar.`,
    logMount: 'Se inicia el montaje de wimhof en español',
    logUnmount: 'Se inicia el DESmontaje de wimhof en español',
    logSaveConfig: 'alguien guardo la config en la pagina WimHofBreathing',
    logLoadConfig: 'alguien abrio la config en la pagina WimHofBreathing',
    logStartExercise: 'alguien comenzó el breathing exercise en la pagina WimHofBreathing',
    roundDisplay: (current, total) => `Ronda ${current + 1} / ${total}`,
    breathDisplay: (current, total) => `Respiración ${current} / ${total}`,
  },
  en: {
    wimHofTitle: 'Wim Hof Breathing',
    wimHofDescription: 'Customize and experience the powerful Wim Hof breathing technique.',
    audioVolumesTitle: 'Audio Volumes',
    backgroundMusicLabel: 'Background Music',
    gongPingLabel: 'Gong and Ping',
    breathingSoundsLabel: 'Breathing Sounds',
    voiceCuesLabel: 'Voice Cues',
    globalSettingsTitle: 'Global Settings',
    selectMusicLabel: 'Select Music',
    ainsaMusic: 'Ainsa',
    blissMusic: 'Bliss',
    inhalationExhalationSoundLabel: 'Inhalation/Exhalation Sound',
    initialWaitTimeLabel: 'Initial Wait Time (sec, 0 to skip)',
    numberOfRoundsLabel: 'Number of Rounds',
    generalSettingsTitle: 'General Settings',
    roundTitle: (index) => `Round ${index + 1}`,
    deleteRoundButton: 'Delete Round',
    inhalationSpeedLabel: 'Inhalation Speed (sec, min. 0.5)',
    exhalationSpeedLabel: 'Exhalation Speed (sec, min. 0.5)',
    breathingPeriodDurationLabel: 'Breathing Period Duration',
    numberOfBreathsOption: 'Number of Breaths',
    totalBreathingTimeOption: 'Total Breathing Time',
    numberOfBreathsInputLabel: 'Number of Breaths (min. 33)',
    totalBreathingTimeInputLabel: 'Total Breathing Time (sec, min. 55)',
    retentionTimeLabel: 'Retention Time (sec, min. 55)',
    voiceCuesFirstBreathsLabel: 'Inhale/Exhale Voice Cues for first X breaths (0 for always)',
    inhaleHoldTimeLabel: 'Inhale and Hold Time',
    roundSettingsTitle: 'Round Settings',
    addRoundButton: '+ Add Round',
    configManagementTitle: 'Configuration Management',
    configNamePlaceholder: 'Configuration Name to Save',
    saveButton: 'Save',
    loadConfigLabel: 'Load Saved Configuration',
    selectOption: 'Select...',
    deleteButtonTitle: 'Delete',
    exportButtonTitle: 'Export',
    importConfigLabel: 'Import Configuration from File',
    configImportSuccess: (name) => `Configuration "${name}" imported successfully.`,
    invalidConfigFile: 'The imported file is not a valid configuration.',
    errorParsingConfigFile: 'Error reading or parsing the configuration file.',
    startBreathingButton: 'Start Breathing',
    initialPreparationPhase: (time) => `Initial Preparation... ${time}`,
    roundPreparationPhase: (time) => `Round Preparation... ${time}`,
    inhalePhase: 'INHALE',
    exhalePhase: 'EXHALE',
    breathingTimeRemaining: (time) => `Breathing time remaining: ${time}`,
    retentionPhase: (time) => `RETENTION: ${time}`,
    inhaleHoldPhase: (time) => `Inhale and Hold: ${time}`,
    recoveryPhase: (time) => `Recovery... ${time}`,
    totalTime: (time) => `Total Time: ${time}`,
    stopExerciseButton: 'Stop Exercise',
    exerciseCompletedTitle: 'Exercise Completed! 🎉',
    roundsCompleted: 'Rounds Completed:',
    totalBreathsApprox: 'Total Breaths (approx.):',
    averageRetentionTime: 'Average Retention Time:',
    totalExerciseTime: 'Total Exercise Time:',
    retentionTimesPerRound: 'Retention Times per Round:',
    roundTime: (index, time) => `Round ${index + 1}: ${time}`,
    backToConfigButton: 'Back to Configuration',
    footerText: (year) => `© ${year} Gus • Aguiar | On a mission to uplift and transform.`,
    logMount: 'Wim Hof component mount (EN)',
    logUnmount: 'Wim Hof component unmount (EN)',
    logSaveConfig: 'Someone saved the config on the WimHofBreathing page',
    logLoadConfig: 'Someone opened the config on the WimHofBreathing page',
    logStartExercise: 'Someone started the breathing exercise on the WimHofBreathing page',
    roundDisplay: (current, total) => `Round ${current + 1} / ${total}`,
    breathDisplay: (current, total) => `Breath ${current} / ${total}`,
  },
};

// Mapeo de audios para fácil acceso, incluyendo ambos idiomas para las voces
const audioAssets = {
  gong: gongAudio,
  ping: pingAudio,
  music: {
    ainsa: ainsaMusic,
    bliss: blissMusic,
  },
  sound: {
    exhaling: soundExhalingAudio,
    inhaling: soundInhalingAudio,
  },
  voice: {
    es: { // Spanish voices
      exhale: voiceExhaleAudioEs,
      inhale: voiceInhaleAudioEs,
      retention: voiceRetentionAudioEs,
      round: voiceRoundAudioEs,
      seg15: voice15SegAudioEs,
      profound: inhaleProfoundEs,
      prepare: voicePrepareAudioEs,
    },
    en: { // English voices
      exhale: voiceExhaleAudioEn,
      inhale: voiceInhaleAudioEn,
      retention: voiceRetentionAudioEn,
      round: voiceRoundAudioEn,
      seg15: voice15SegAudioEn,
      profound: inhaleProfoundEn,
      prepare: voicePrepareAudioEn,
    },
  },
};

// Component for a single round configuration
const RoundSettings = ({
  roundIndex,
  roundConfig,
  onUpdateRoundSetting,
  isGeneralSetting,
  onDeleteRound,
  canBeDeleted,
  t, // Passed translations object
}) => {
  return (
    <div
      className={`relative p-4 rounded-lg border mb-4 ${
        isGeneralSetting
          ? 'border-purple-600 bg-purple-900/20'
          : 'border-gray-700 bg-gray-800'
      }`}
    >
      {/* NEW: Button to delete the round */}
      {!isGeneralSetting && canBeDeleted && (
        <button
          onClick={() => onDeleteRound(roundIndex)}
          className="absolute top-2 right-2 p-1 bg-red-600/50 text-white rounded-full hover:bg-red-600 transition duration-300 z-10"
          title={t.deleteRoundButton}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <h4 className="text-xl font-semibold mb-3 text-purple-300">
        {isGeneralSetting ? t.generalSettingsTitle : t.roundTitle(roundIndex)}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-400 text-sm mb-1">
            {t.inhalationSpeedLabel}
          </label>
          <input
            type="number"
            min="0.5"
            step="0.1"
            value={roundConfig.inhalationSpeed}
            onChange={(e) =>
              onUpdateRoundSetting(
                roundIndex,
                'inhalationSpeed',
                parseFloat(e.target.value)
              )
            }
            className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div>
          <label className="block text-gray-400 text-sm mb-1">
            {t.exhalationSpeedLabel}
          </label>
          <input
            type="number"
            min="0.5"
            step="0.1"
            value={roundConfig.exhalationSpeed}
            onChange={(e) =>
              onUpdateRoundSetting(
                roundIndex,
                'exhalationSpeed',
                parseFloat(e.target.value)
              )
            }
            className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Selector de tipo de duración de respiración */}
        <div className="col-span-full">
          <label className="block text-gray-400 text-sm mb-1">
            {t.breathingPeriodDurationLabel}
          </label>
          <div className="flex space-x-4 mb-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name={`breathingType-${roundIndex}`}
                value="breaths"
                checked={roundConfig.breathingDurationType === 'breaths'}
                onChange={() =>
                  onUpdateRoundSetting(roundIndex, 'breathingDurationType', 'breaths')
                }
                className="form-radio text-purple-600"
              />
              <span className="ml-2 text-gray-300">{t.numberOfBreathsOption}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name={`breathingType-${roundIndex}`}
                value="time"
                checked={roundConfig.breathingDurationType === 'time'}
                onChange={() =>
                  onUpdateRoundSetting(roundIndex, 'breathingDurationType', 'time')
                }
                className="form-radio text-purple-600"
              />
              <span className="ml-2 text-gray-300">{t.totalBreathingTimeOption}</span>
            </label>
          </div>
        </div>

        {/* Conditional field based on duration type */}
        {roundConfig.breathingDurationType === 'breaths' ? (
          <div>
            <label className="block text-gray-400 text-sm mb-1">
              {t.numberOfBreathsInputLabel}
            </label>
            <input
              type="number"
              min="33"
              value={roundConfig.breathsPerRound}
              onChange={(e) =>
                onUpdateRoundSetting(
                  roundIndex,
                  'breathsPerRound',
                  parseInt(e.target.value, 10)
                )
              }
              className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        ) : (
          <div>
            <label className="block text-gray-400 text-sm mb-1">
              {t.totalBreathingTimeInputLabel}
            </label>
            <input
              type="number"
              min="55"
              value={roundConfig.totalBreathingTime}
              onChange={(e) =>
                onUpdateRoundSetting(
                  roundIndex,
                  'totalBreathingTime',
                  parseInt(e.target.value, 10)
                )
              }
              className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        )}

        <div>
          <label className="block text-gray-400 text-sm mb-1">
            {t.retentionTimeLabel}
          </label>
          <input
            type="number"
            min="55"
            value={roundConfig.retentionTime}
            onChange={(e) =>
              onUpdateRoundSetting(
                roundIndex,
                'retentionTime',
                parseInt(e.target.value, 10)
              )
            }
            className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        {/* New configuration: Voice only in the first X breaths */}
        <div>
          <label className="block text-gray-400 text-sm mb-1">
            {t.voiceCuesFirstBreathsLabel}
          </label>
          <input
            type="number"
            min="0"
            value={roundConfig.voiceCuesFirstBreaths}
            onChange={(e) =>
              onUpdateRoundSetting(roundIndex, 'voiceCuesFirstBreaths', parseInt(e.target.value, 10))
            }
            className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        {/* NEW: Field for short retention time */}
        <div>
          <label className="block text-gray-400 text-sm mb-1">
            {t.inhaleHoldTimeLabel}
          </label>
          <input
            type="number"
            min="5"
            value={roundConfig.shortRetentionTime}
            onChange={(e) =>
              onUpdateRoundSetting(roundIndex, 'shortRetentionTime', parseInt(e.target.value, 10))
            }
            className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>
    </div>
  );
};

const WimHofBreathing = () => {
  const location = useLocation();
  
  // 1. Obtener parámetros de búsqueda
  const searchParams = new URLSearchParams(location.search);
  const lgParam = searchParams.get('lg');

  // 2. Determinar idioma
  let currentLang;

  // Prioridad 1: Si existe el parámetro ?lg=en o ?lg=es, úsalo
  if (lgParam === 'en' || lgParam === 'es') {
    currentLang = lgParam;
  } 
  // Prioridad 2: Si la ruta empieza explícitamente con /en/ (opcional, para mantener consistencia)
  else if (location.pathname.startsWith('/en/')) {
    currentLang = 'en';
  } 
  // Prioridad 3: Default es español (cubre '/es/', rutas sin prefijo, etc.)
  else {
    currentLang = 'es';
  }

  const t = translations[currentLang]; 

  // Animation component
  const { ref: componentRef, inView: componentInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // --- Configuration States ---
  const [numberOfRounds, setNumberOfRounds] = useState(1);
  const [withBackgroundMusic, setWithBackgroundMusic] = useState(true);
  const [selectedMusic, setSelectedMusic] = useState('ainsa'); // 'ainsa' or 'bliss'
  const [withBreathingSound, setWithBreathingSound] = useState(true);
  const [startWaitTime, setStartWaitTime] = useState(7); // Initial wait time in seconds

  // Volume states
  const [musicVolume, setMusicVolume] = useState(0.7);
  const [gongPingVolume, setGongPingVolume] = useState(0.8);
  const [breathingSoundVolume, setBreathingSoundVolume] = useState(0.5);
  const [voiceVolume, setVoiceVolume] = useState(1.0);


  const defaultRoundSettings = {
    inhalationSpeed: 2, // seconds
    exhalationSpeed: 2, // seconds
    breathingDurationType: 'breaths', // 'breaths' or 'time'
    breathsPerRound: 33, // number of breaths
    totalBreathingTime: 60, // seconds (if breathingDurationType is 'time')
    retentionTime: 60, // seconds
    voiceCuesFirstBreaths: 2, // number of breaths with voice cues
    shortRetentionTime: 15, // Short retention time after main retention
  };

  const [generalSettings, setGeneralSettings] =
    useState(defaultRoundSettings);
  const [roundSpecificSettings, setRoundSpecificSettings] = useState([]); // Array of objects for each round

  const [savedConfigurations, setSavedConfigurations] = useState([]);
  const [selectedSavedConfig, setSelectedSavedConfig] = useState(''); // ID of the selected config
  const [isLoadingConfig, setIsLoadingConfig] = useState(false); // Flag to control config loading


  // --- Exercise Execution States ---
  const [isExercising, setIsExercising] = useState(false);
  const [currentRound, setCurrentRound] = useState(0); // 0-indexed
  const [currentBreath, setCurrentBreath] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('idle'); // 'idle', 'start_countdown', 'preparation', 'inhalation', 'exhalation', 'retention', 'inhaleRetain15', 'finished'
  const [phaseTimer, setPhaseTimer] = useState(0);
  const [breathingTimer, setBreathingTimer] = useState(0); // For 'time' breathing mode
  const [totalTimeElapsed, setTotalTimeElapsed] = useState(0);
  const [roundRetentionTimes, setRoundRetentionTimes] = useState([]); // For summary
  const [exerciseSummary, setExerciseSummary] = useState(null);

  // --- References to Audio objects ---
  const audioRefs = useRef({});

  // Initialize audio objects when component mounts or language changes
  useEffect(() => {
    // Clear existing audio objects to avoid memory leaks on language change
    for (const key in audioRefs.current) {
        if (audioRefs.current[key]) {
            audioRefs.current[key].pause();
            audioRefs.current[key] = null; // Dereference
        }
    }
    audioRefs.current = {}; // Reset the ref

    // Initialize new Audio objects based on audioAssets
    // Handle non-voice assets normally
    Object.keys(audioAssets).forEach((key) => {
      if (key !== 'voice') {
        if (typeof audioAssets[key] === 'string') {
          audioRefs.current[key] = new Audio(audioAssets[key]);
        } else if (typeof audioAssets[key] === 'object') {
          Object.keys(audioAssets[key]).forEach((subKey) => {
            audioRefs.current[`${key}_${subKey}`] = new Audio(audioAssets[key][subKey]);
          });
        }
      }
    });

    // Handle voice assets dynamically based on currentLang
    const voiceAssetsForLang = audioAssets.voice[currentLang];
    Object.keys(voiceAssetsForLang).forEach((voiceKey) => {
        audioRefs.current[`voice_${voiceKey}`] = new Audio(voiceAssetsForLang[voiceKey]);
    });

    // Preload (optional, some browsers block auto-play without user interaction)
    for (const key in audioRefs.current) {
      if (audioRefs.current[key]) {
        audioRefs.current[key].load();
      }
    }

    return () => {
        // Cleanup on unmount or language change
        for (const key in audioRefs.current) {
            if (audioRefs.current[key]) {
                audioRefs.current[key].pause();
                audioRefs.current[key] = null;
            }
        }
    };
  }, [currentLang]); // Re-run effect when currentLang changes


  const setAudioVolume = useCallback((key, volume) => {
    const audio = audioRefs.current[key];
    if (audio) {
      audio.volume = volume;
    }
  }, []);

  // Update audio volumes when volume states change
  useEffect(() => {
    setAudioVolume('music_ainsa', musicVolume);
    setAudioVolume('music_bliss', musicVolume);
  }, [musicVolume, setAudioVolume]);

  useEffect(() => {
    setAudioVolume('gong', gongPingVolume);
    setAudioVolume('ping', gongPingVolume);
  }, [gongPingVolume, setAudioVolume]);

  useEffect(() => {
    setAudioVolume('sound_exhaling', breathingSoundVolume);
    setAudioVolume('sound_inhaling', breathingSoundVolume);
  }, [breathingSoundVolume, setAudioVolume]);

  useEffect(() => {
    setAudioVolume('voice_exhale', voiceVolume);
    setAudioVolume('voice_inhale', voiceVolume);
    setAudioVolume('voice_retention', voiceVolume);
    setAudioVolume('voice_round', voiceVolume);
    setAudioVolume('voice_seg15', voiceVolume);
    setAudioVolume('voice_profound', voiceVolume);
    setAudioVolume('voice_prepare', voiceVolume);
  }, [voiceVolume, setAudioVolume]);


  const playAudio = useCallback((key, loop = false) => {
    const audio = audioRefs.current[key];
    if (audio) {
      audio.currentTime = 0; // Always restart from the beginning
      audio.loop = loop;
      audio.play().catch((e) => console.log('Error playing audio:', key, e));
    }
  }, []);

  const stopAudio = useCallback((key) => {
    const audio = audioRefs.current[key];
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  const stopAllAudios = useCallback(() => {
    for (const key in audioRefs.current) {
      if (audioRefs.current[key]) {
        audioRefs.current[key].pause();
        audioRefs.current[key].currentTime = 0;
      }
    }
  }, []);

  // Helper function to format seconds to "M:SS" or "SSs"
  const formatTime = useCallback((totalSeconds) => {
    if (isNaN(totalSeconds) || totalSeconds < 0) {
      return "0s";
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60); // Round seconds
    if (minutes > 0) {
      return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    }
    return `${seconds}s`;
  }, []);

  // --- Effect to initialize and synchronize round settings ---
  useEffect(() => {
    if (isLoadingConfig) return;

    // IMPORTANTE: Si numberOfRounds está vacío, usamos 1 para la lógica de creación de arrays

    setRoundSpecificSettings(currentSettings => {
      const effectiveRounds = numberOfRounds === '' ? currentSettings.length : numberOfRounds;

      const currentLength = currentSettings.length;

      if (effectiveRounds < currentLength) {
        return currentSettings.slice(0, effectiveRounds);
      }

      if (effectiveRounds > currentLength) {
        const newRounds = [];
        const numToAdd = effectiveRounds - currentLength;
        const baseSettings = currentLength > 0 ? currentSettings[currentLength - 1] : generalSettings;
        for (let i = 0; i < numToAdd; i++) {
          newRounds.push({ ...baseSettings });
        }
        return [...currentSettings, ...newRounds];
      }

      if (currentLength === 0 && effectiveRounds > 0) {
          return Array.from({ length: effectiveRounds }, () => ({...generalSettings}));
      }

      return currentSettings;
    });
  }, [numberOfRounds, generalSettings, isLoadingConfig]);


  // --- Load saved configurations on startup ---
  useEffect(() => {
    const storedConfigs = localStorage.getItem('wimhof_configs');
    if (storedConfigs) {
      setSavedConfigurations(JSON.parse(storedConfigs));
    }
  }, []);

  // --- Configuration management functions ---
const handleUpdateGeneralSetting = (key, value) => {
    setGeneralSettings((prev) => ({ ...prev, [key]: value }));

    // Propagar el cambio retornando el nuevo arreglo mapeado
    setRoundSpecificSettings((prevRounds) =>
      prevRounds.map((round) => ({ ...round, [key]: value }))
    );
  };

  const handleUpdateRoundSetting = (roundIndex, key, value) => {
    setRoundSpecificSettings((prevRounds) =>
      prevRounds.map((round, idx) =>
        idx === roundIndex ? { ...round, [key]: value } : round
      )
    );
  };

  // NEW FUNCTION: Add a round
  const handleAddRound = () => {
    setNumberOfRounds(prev => prev + 1);
  };

  // NEW FUNCTION: Delete a specific round
  const handleDeleteRound = (roundIndexToDelete) => {
    if (numberOfRounds <= 1 || numberOfRounds === '') return; // Do not allow deleting the last round
    setNumberOfRounds(prev => prev - 1);
    setRoundSpecificSettings(prev => prev.filter((_, index) => index !== roundIndexToDelete));
  };

  const handleSubmit = async (messageKey) => {
    sendCodedLog({
      message: t[messageKey],
      event: 'wimhof_action',
        ...Common.getCommonLogData(location)
    });
  };

  const saveConfiguration = (name) => {
    if (!name.trim()) return;
    const newConfig = {
      id: uuidv4(),
      name,
      numberOfRounds,
      withBackgroundMusic,
      selectedMusic,
      withBreathingSound,
      startWaitTime,
      musicVolume,
      gongPingVolume,
      breathingSoundVolume,
      voiceVolume,
      generalSettings,
      roundSpecificSettings,
    };
    const updatedConfigs = [...savedConfigurations, newConfig];
    setSavedConfigurations(updatedConfigs);
    localStorage.setItem('wimhof_configs', JSON.stringify(updatedConfigs));
    setSelectedSavedConfig(newConfig.id);
    handleSubmit('logSaveConfig');
  };

  const loadConfiguration = (configId) => {
    const configToLoad = savedConfigurations.find((c) => c.id === configId);
    if (configToLoad) {
      setIsLoadingConfig(true);

      setNumberOfRounds(configToLoad.numberOfRounds);
      setWithBackgroundMusic(configToLoad.withBackgroundMusic);
      setSelectedMusic(configToLoad.selectedMusic);
      setWithBreathingSound(configToLoad.withBreathingSound);
      setStartWaitTime(configToLoad.startWaitTime ?? 5);
      setMusicVolume(configToLoad.musicVolume ?? 0.7);
      setGongPingVolume(configToLoad.gongPingVolume ?? 0.8);
      setBreathingSoundVolume(configToLoad.breathingSoundVolume ?? 0.5);
      setVoiceVolume(configToLoad.voiceVolume ?? 1.0);
      setGeneralSettings(configToLoad.generalSettings);
      setRoundSpecificSettings(configToLoad.roundSpecificSettings); // Direct load
      setSelectedSavedConfig(configId);

      setTimeout(() => setIsLoadingConfig(false), 100); // Small delay to ensure states propagate
    }
    handleSubmit('logLoadConfig');
  };

  const deleteConfiguration = (configId) => {
    const updatedConfigs = savedConfigurations.filter((c) => c.id !== configId);
    setSavedConfigurations(updatedConfigs);
    localStorage.setItem('wimhof_configs', JSON.stringify(updatedConfigs));
    if (selectedSavedConfig === configId) {
      setSelectedSavedConfig('');
    }
  };

  const exportConfiguration = (configId) => {
    const configToExport = savedConfigurations.find((c) => c.id === configId);
    if (configToExport) {
      const blob = new Blob([JSON.stringify(configToExport, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      saveAs(blob, `${configToExport.name}.json`);
    }
  };

  const importConfiguration = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedConfig = JSON.parse(e.target.result);
          if (
            importedConfig.id &&
            importedConfig.name &&
            importedConfig.numberOfRounds !== undefined
          ) {
            const configWithDefaults = {
                ...importedConfig,
                musicVolume: importedConfig.musicVolume ?? 0.7,
                gongPingVolume: importedConfig.gongPingVolume ?? 0.8,
                breathingSoundVolume: importedConfig.breathingSoundVolume ?? 0.5,
                voiceVolume: importedConfig.voiceVolume ?? 1.0,
                startWaitTime: importedConfig.startWaitTime ?? 5,
                generalSettings: {
                    ...defaultRoundSettings,
                    ...importedConfig.generalSettings,
                    voiceCuesFirstBreaths: importedConfig.generalSettings?.voiceCuesFirstBreaths ?? 2,
                    shortRetentionTime: importedConfig.generalSettings?.shortRetentionTime ?? 15,
                },
                roundSpecificSettings: importedConfig.roundSpecificSettings.map(rs => ({
                    ...defaultRoundSettings,
                    ...rs,
                    voiceCuesFirstBreaths: rs.voiceCuesFirstBreaths ?? 2,
                    shortRetentionTime: rs.shortRetentionTime ?? 15,
                }))
            };
            const updatedConfigs = [...savedConfigurations, configWithDefaults];
            setSavedConfigurations(updatedConfigs);
            localStorage.setItem(
              'wimhof_configs',
              JSON.stringify(updatedConfigs)
            );
            alert(t.configImportSuccess(importedConfig.name));
            setTimeout(()=> {
              loadConfiguration(importedConfig.id);
            }, 333)
          } else {
            alert(t.invalidConfigFile);
          }
        } catch (error) {
          alert(t.errorParsingConfigFile);
          console.error('Error importing config:', error);
        }
      };
      reader.readAsText(file);
    }
  };

  // --- Exercise Logic ---
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const breathingIntervalRef = useRef(null);

  const startExercise = () => {
    setIsExercising(true);
    setCurrentRound(0);
    setRoundRetentionTimes([]);
    setExerciseSummary(null);
    setTotalTimeElapsed(0);

    setCurrentPhase('start_countdown');
    playAudio('voice_prepare', false);

    setPhaseTimer(startWaitTime);

    if (startWaitTime > 0) {
      let timer = startWaitTime;
      intervalRef.current = setInterval(() => {
        timer--;
        setPhaseTimer(timer);
        if (timer <= 0) {
          clearInterval(intervalRef.current);
          startRound(0);
        }
      }, 1000);
    } else {
      startRound(0);
    }

    handleSubmit('logStartExercise');
  };

  const stopExercise = () => {
    setIsExercising(false);
    setCurrentPhase('idle');
    clearInterval(intervalRef.current);
    clearInterval(breathingIntervalRef.current);
    clearTimeout(timeoutRef.current);
    stopAllAudios();
  };

  const startRound = (roundNum) => {
    if (roundNum >= numberOfRounds) {
      finishExercise();
      return;
    }

    clearInterval(intervalRef.current);
    clearInterval(breathingIntervalRef.current);
    clearTimeout(timeoutRef.current);

    setCurrentRound(roundNum);
    setCurrentBreath(0);
    setCurrentPhase('preparation');
    setPhaseTimer(3);

    playAudio('voice_round', false);
    
    if (withBackgroundMusic) {
      stopAudio(`music_${selectedMusic}`);
      playAudio(`music_${selectedMusic}`, true);
    }

    timeoutRef.current = setTimeout(() => {
      startBreathingPhase(roundNum);
    }, 3000);
  };

  const startBreathingPhase = (roundNum) => {
    setCurrentPhase('inhalation');
    const config = roundSpecificSettings[roundNum];
    const {
      inhalationSpeed,
      exhalationSpeed,
      breathingDurationType,
      breathsPerRound,
      totalBreathingTime,
      voiceCuesFirstBreaths,
    } = config;

    let breathCount = 0;
    let currentBreathPhase = 'inhalation';
    let elapsedBreathingTime = 0;

    setBreathingTimer(
        breathingDurationType === 'time' ? totalBreathingTime : 0
    );

    const doBreathCycle = () => {
      if (
        (breathingDurationType === 'time' && elapsedBreathingTime >= totalBreathingTime) ||
        (breathingDurationType === 'breaths' && breathCount >= breathsPerRound)
      ) {
        clearInterval(breathingIntervalRef.current);
        playAudio('voice_profound', false);
        setTimeout(() => playAudio('sound_inhaling'), 500);
        setCurrentPhase('inhalation');

        setTimeout(() => {
          setTimeout(() => {
            setCurrentPhase('exhalation');
            playAudio('voice_exhale');
          }, 1500);
          setTimeout(() => playAudio('sound_exhaling'), 2000);
          setTimeout(() => {
            setTimeout(() => startRetentionPhase(roundNum), 500);
          }, 3500);
        }, 1000);
        return;
      }

      if (currentBreathPhase === 'inhalation') {
        setCurrentPhase('inhalation');
        setCurrentBreath((prev) => prev + 1);
        setPhaseTimer(inhalationSpeed);
        if (voiceCuesFirstBreaths === 0 || breathCount < voiceCuesFirstBreaths) {
          playAudio('voice_inhale');
        }
        if (withBreathingSound) playAudio('sound_inhaling');

        timeoutRef.current = setTimeout(() => {
          elapsedBreathingTime += inhalationSpeed;
          currentBreathPhase = 'exhalation';
          doBreathCycle();
        }, inhalationSpeed * 1000);
      } else { // Exhalation
        setCurrentPhase('exhalation');
        setPhaseTimer(exhalationSpeed);
        if (voiceCuesFirstBreaths === 0 || breathCount < voiceCuesFirstBreaths) {
          playAudio('voice_exhale');
        }
        if (withBreathingSound) playAudio('sound_exhaling');

        timeoutRef.current = setTimeout(() => {
          elapsedBreathingTime += exhalationSpeed;
          breathCount++;
          currentBreathPhase = 'inhalation';
          doBreathCycle();
        }, exhalationSpeed * 1000);
      }
    };

    doBreathCycle();

    if (breathingDurationType === 'time') {
        breathingIntervalRef.current = setInterval(() => {
            setBreathingTimer(prev => Math.max(0, prev - 1));
        }, 1000);
    }
  };

  const startRetentionPhase = (roundNum) => {
    setCurrentPhase('retention');
    const { retentionTime } = roundSpecificSettings[roundNum];
    setPhaseTimer(retentionTime);

    if (withBackgroundMusic) stopAudio(`music_${selectedMusic}`);
    if (audioRefs.current?.voice_retention) audioRefs.current.voice_retention.loop = false;

    playAudio('ping');
    playAudio('voice_retention', false);

    let timer = retentionTime;
    intervalRef.current = setInterval(() => {
      timer--;
      setPhaseTimer(timer);
      if (timer <= 0) {
        clearInterval(intervalRef.current);
        playAudio('ping');
        stopAudio('voice_retention');
        setRoundRetentionTimes((prev) => [...prev, retentionTime]);

        setTimeout(() => playAudio('voice_profound', false), 333);
        setTimeout(() => playAudio('sound_inhaling'), 1000);
        setCurrentPhase('inhalation');
        setTimeout(() => startInhaleRetainPhase(roundNum), 3000);
      }
    }, 1000);
  };

  const startInhaleRetainPhase = (currentRoundNum) => {
    const { shortRetentionTime } = roundSpecificSettings[currentRoundNum];
    const shortRetentionDuration = shortRetentionTime ?? 15;

    setCurrentPhase('inhaleRetain15');
    setPhaseTimer(shortRetentionDuration);

    if (withBackgroundMusic) stopAudio(`music_${selectedMusic}`);
    playAudio('voice_seg15', false);

    let timer = shortRetentionDuration;
    intervalRef.current = setInterval(() => {
      timer--;
      setPhaseTimer(timer);
      if (timer <= 0) {
        clearInterval(intervalRef.current);
        if (withBreathingSound) {
          setTimeout(() => playAudio('ping'), 111);
          setTimeout(() => playAudio('voice_exhale'), 333);
          setTimeout(() => playAudio('sound_exhaling'), 666);
        }

        if (currentRoundNum + 1 >= numberOfRounds) {
          finishExercise();
        } else {
          setCurrentPhase('recovery');
          setPhaseTimer(3);
          timeoutRef.current = setTimeout(() => {
            startRound(currentRoundNum + 1);
          }, 3000);
        }
      }
    }, 1000);
  };

  const finishExercise = useCallback(() => {
    stopAllAudios();
    playAudio('gong');
    setIsExercising(true);
    setCurrentPhase('finished');

    const totalBreaths = roundSpecificSettings.reduce(
      (acc, r) => acc + (r.breathingDurationType === 'breaths' ? r.breathsPerRound : Math.round(r.totalBreathingTime / (r.inhalationSpeed + r.exhalationSpeed))),
      0
    );
    const totalRetention = roundRetentionTimes.reduce((acc, t) => acc + t, 0);

    setExerciseSummary({
      totalRounds: numberOfRounds,
      totalBreaths: Math.round(totalBreaths),
      averageRetentionTime: totalRetention / numberOfRounds || 0,
      totalTime: totalTimeElapsed,
      roundRetentionTimes,
    });
  }, [numberOfRounds, roundSpecificSettings, roundRetentionTimes, totalTimeElapsed, playAudio, stopAllAudios]);

  useEffect(() => {
    let timerId;
    if (isExercising && currentPhase !== 'finished' && currentPhase !== 'idle') {
      timerId = setInterval(() => {
        setTotalTimeElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timerId);
  }, [isExercising, currentPhase]);


  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col items-center">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">
        <div
          ref={componentRef}
          className={`w-full max-w-4xl mx-auto px-4 py-8 bg-gray-900 rounded-xl shadow-lg border border-gray-700
            transition-all duration-700 ease-in-out ${
              componentInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          <h2 className="text-3xl font-bold text-white text-center mb-6">
            {t.wimHofTitle}
          </h2>
          <p className="text-gray-300 text-center mb-8">
            {t.wimHofDescription}
          </p>

          <div className="p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700 mb-6">
              <h3 className="text-2xl font-bold text-orange-400 mb-4">{t.audioVolumesTitle}</h3>
              <div className="space-y-4">
                  <div>
                      <label className="block text-gray-400 text-sm mb-1">{t.backgroundMusicLabel}</label>
                      <input
                          type="range" min="0" max="1" step="0.05" value={musicVolume}
                          onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 accent-purple-500"
                      />
                  </div>
                  <div>
                      <label className="block text-gray-400 text-sm mb-1">{t.gongPingLabel}</label>
                      <input
                          type="range" min="0" max="1" step="0.05" value={gongPingVolume}
                          onChange={(e) => setGongPingVolume(parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 accent-purple-500"
                      />
                  </div>
                  <div>
                      <label className="block text-gray-400 text-sm mb-1">{t.breathingSoundsLabel}</label>
                      <input
                          type="range" min="0" max="1" step="0.05" value={breathingSoundVolume}
                          onChange={(e) => setBreathingSoundVolume(parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 accent-purple-500"
                      />
                  </div>
                  <div>
                      <label className="block text-gray-400 text-sm mb-1">{t.voiceCuesLabel}</label>
                      <input
                          type="range" min="0" max="1" step="0.05" value={voiceVolume}
                          onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 accent-purple-500"
                      />
                  </div>
              </div>
          </div>

          {!isExercising ? ( // Configuration Mode
            <div className="space-y-6 w-full">
              <div className="p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700">
                <h3 className="text-2xl font-bold text-sky-400 mb-4">
                  {t.globalSettingsTitle}
                </h3>
                <div className="mb-4">
                  <CustomToggle id="withBackgroundMusic" label={t.backgroundMusicLabel} isOn={withBackgroundMusic} handleToggle={() => setWithBackgroundMusic(!withBackgroundMusic)} />
                  {withBackgroundMusic && (
                    <div className="mt-2 pl-7 border-l border-gray-700 ml-2">
                      <label htmlFor="selectMusic" className="block text-gray-400 text-sm mb-1">{t.selectMusicLabel}</label>
                      <select
                        id="selectMusic" value={selectedMusic} onChange={(e) => setSelectedMusic(e.target.value)}
                        className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="ainsa">{t.ainsaMusic}</option>
                        <option value="bliss">{t.blissMusic}</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <CustomToggle id="withBreathingSound" label={t.inhalationExhalationSoundLabel} isOn={withBreathingSound} handleToggle={() => setWithBreathingSound(!withBreathingSound)} />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 text-sm mb-1">
                    {t.initialWaitTimeLabel}
                  </label>
                  <input
                    type="number" min="0" value={startWaitTime}
                    onChange={(e) => setStartWaitTime(parseInt(e.target.value, 10))}
                    className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 text-sm mb-1">
                    {t.numberOfRoundsLabel}
                  </label>
                  <input
                    type="number" min="1" 
                    value={numberOfRounds}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Si el usuario borra todo, permitimos el string vacío temporalmente
                      if (val === '') {
                        setNumberOfRounds('');
                      } else {
                        // Si escribe un número, lo convertimos y nos aseguramos que sea al menos 1
                        const parsed = parseInt(val, 10);
                        setNumberOfRounds(isNaN(parsed) ? '' : Math.max(1, parsed));
                      }
                    }}
                    className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <RoundSettings
                  roundIndex={-1} roundConfig={generalSettings}
                  onUpdateRoundSetting={(_, key, value) => handleUpdateGeneralSetting(key, value)}
                  isGeneralSetting={true}
                  t={t} // Pass translations
                />
              </div>

              {(numberOfRounds > 0 || numberOfRounds === '') && (
                <div className="p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700">
                  <h3 className="text-2xl font-bold text-teal-400 mb-4">
                    {t.roundSettingsTitle}
                  </h3>
                  {roundSpecificSettings.map((roundConfig, index) => (
                    <RoundSettings
                      key={index}
                      roundIndex={index}
                      roundConfig={roundConfig}
                      onUpdateRoundSetting={handleUpdateRoundSetting}
                      onDeleteRound={handleDeleteRound}
                      canBeDeleted={roundSpecificSettings.length > 1}
                      t={t} // Pass translations
                    />
                  ))}
                  {/* NEW: Add Round Button */}
                  <div className="mt-4">
                    <button
                      onClick={handleAddRound}
                      className="w-full py-2 px-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-600 transition duration-300"
                    >
                      {t.addRoundButton}
                    </button>
                  </div>
                </div>
              )}
              <div className="p-4 sm:p-6 bg-gray-800 rounded-lg shadow-md border border-gray-700 w-full max-w-full overflow-hidden">
                <h3 className="text-xl sm:text-2xl font-bold text-yellow-400 mb-4 break-words">
                  {t.configManagementTitle}
                </h3>

                {/* Formulario de Guardar: en móvil uno debajo del otro, en desktop en fila */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
                  <input
                    type="text" 
                    placeholder={t.configNamePlaceholder}
                    className="w-full sm:flex-1 min-w-0 p-2.5 rounded-md bg-gray-700 text-white placeholder-gray-400 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                    id="config-name-input"
                  />
                  <button
                    onClick={() => saveConfiguration(document.getElementById('config-name-input').value)}
                    className="w-full sm:w-auto shrink-0 py-2.5 px-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold rounded-lg shadow-md hover:from-orange-600 hover:to-yellow-600 transition duration-300 text-sm sm:text-base"
                  >
                    {t.saveButton}
                  </button>
                </div>

                {/* Lista de configuraciones guardadas */}
                {savedConfigurations.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-gray-400 text-xs sm:text-sm mb-1">
                      {t.loadConfigLabel}
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedSavedConfig} 
                        onChange={(e) => loadConfiguration(e.target.value)}
                        className="flex-1 min-w-0 p-2.5 rounded-md bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base truncate"
                      >
                        <option value="">{t.selectOption}</option>
                        {savedConfigurations.map((config) => (
                          <option key={config.id} value={config.id}>
                            {config.name}
                          </option>
                        ))}
                      </select>
                      {selectedSavedConfig && (
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            onClick={() => deleteConfiguration(selectedSavedConfig)}
                            className="p-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300" 
                            title={t.deleteButtonTitle}
                            aria-label={t.deleteButtonTitle}
                          >
                            🗑️
                          </button>
                          <button
                            onClick={() => exportConfiguration(selectedSavedConfig)}
                            className="p-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300" 
                            title={t.exportButtonTitle}
                            aria-label={t.exportButtonTitle}
                          >
                            📤
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Selector de archivo */}
                <div>
                  <label className="block w-full text-gray-400 text-xs sm:text-sm mb-1">
                    {t.importConfigLabel}
                  </label>
                  <input
                    type="file" 
                    accept=".json" 
                    onChange={importConfiguration}
                    className="block w-full text-xs sm:text-sm text-gray-400 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-gray-700 file:text-orange-400 hover:file:bg-gray-600 cursor-pointer"
                  />
                </div>
              </div>
              <button
                onClick={startExercise}
                className="w-full py-4 px-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg shadow-lg hover:from-green-600 hover:to-emerald-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-xl"
              >
                {t.startBreathingButton}
              </button>
            </div>
          ) : currentPhase !== 'finished' ? ( // Active Exercise Mode
            <div className="flex flex-col items-center justify-center p-8 bg-gray-800 rounded-lg shadow-xl border border-gray-700 min-h-[400px]">
                <>
                  <p className="text-2xl text-purple-400 font-bold mb-4">
                    {t.roundDisplay(currentRound, numberOfRounds)}
                  </p>
                  {currentPhase === 'start_countdown' && (
                    <h3 className="text-4xl text-white font-extrabold animate-pulse">
                      {t.initialPreparationPhase(formatTime(phaseTimer))}
                    </h3>
                  )}
                  {currentPhase === 'preparation' && (
                    <h3 className="text-4xl text-white font-extrabold animate-pulse">
                      {t.roundPreparationPhase(formatTime(phaseTimer))}
                    </h3>
                  )}
                  {(currentPhase === 'inhalation' || currentPhase === 'exhalation') && (
                    <div className="text-center">
                        <h3 className={`text-4xl font-extrabold ${currentPhase === 'inhalation' ? 'text-green-400' : 'text-red-400'}`}>
                            {currentPhase === 'inhalation' ? t.inhalePhase : t.exhalePhase}
                            <span className="ml-4 text-2xl text-gray-300">
                                ({phaseTimer.toFixed(1)}s)
                            </span>
                        </h3>
                        {roundSpecificSettings[currentRound]?.breathingDurationType === 'breaths' ? (
                            <p className="text-xl text-gray-300 mt-2">
                                {t.breathDisplay(currentBreath, roundSpecificSettings[currentRound]?.breathsPerRound)}
                            </p>
                        ) : (
                            <p className="text-xl text-gray-300 mt-2">
                                {t.breathingTimeRemaining(formatTime(breathingTimer))}
                            </p>
                        )}
                    </div>
                  )}
                  {currentPhase === 'retention' && (
                    <h3 className="text-5xl text-yellow-400 font-extrabold animate-bounce">
                      {t.retentionPhase(formatTime(phaseTimer))}
                    </h3>
                  )}
                  {currentPhase === 'inhaleRetain15' && (
                    <h3 className="text-4xl text-blue-400 font-extrabold animate-pulse">
                      {t.inhaleHoldPhase(formatTime(phaseTimer))}
                    </h3>
                  )}
                  {currentPhase === 'recovery' && (
                    <h3 className="text-4xl text-blue-400 font-extrabold animate-pulse">
                      {t.recoveryPhase(formatTime(phaseTimer))}
                    </h3>
                  )}
                  <p className="text-gray-400 mt-6 text-xl">
                    {t.totalTime(formatTime(totalTimeElapsed))}
                  </p>
                  <button
                    onClick={stopExercise}
                    className="mt-8 py-3 px-6 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 transition duration-300"
                  >
                    {t.stopExerciseButton}
                  </button>
                </>
            </div>
          ) : ( // Summary Mode
                <div className="text-center animate-fade-in-up">
                    <h3 className="text-4xl font-bold text-green-500 mb-6 ">
                        {t.exerciseCompletedTitle}
                    </h3>
                    {exerciseSummary && (
                        <div className="p-6 bg-gray-900 rounded-lg border border-gray-700 text-left space-y-3">
                            <p className="text-xl text-white">
                                <span className="font-bold text-purple-300">{t.roundsCompleted}</span>{' '}
                                {exerciseSummary.totalRounds}
                            </p>
                            <p className="text-xl text-white">
                                <span className="font-bold text-purple-300">{t.totalBreathsApprox}</span>{' '}
                                {exerciseSummary.totalBreaths}
                            </p>
                            <p className="text-xl text-white">
                                <span className="font-bold text-purple-300">{t.averageRetentionTime}</span>{' '}
                                {formatTime(Math.round(exerciseSummary.averageRetentionTime))}
                            </p>
                            <p className="text-xl text-white">
                                <span className="font-bold text-purple-300">{t.totalExerciseTime}</span>{' '}
                                {formatTime(exerciseSummary.totalTime)}
                            </p>
                            <div className="text-xl text-white">
                                <span className="font-bold text-purple-300">{t.retentionTimesPerRound}{' '}</span>
                                <ul className="list-disc list-inside ml-4">
                                    {exerciseSummary.roundRetentionTimes.map(
                                    (time, idx) => (
                                        <li key={idx}>{t.roundTime(idx, formatTime(time))}</li>
                                    )
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => {
                            setIsExercising(false);
                            setCurrentPhase('idle');
                        }}
                        className="mt-8 py-3 px-6 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 transition duration-300"
                    >
                        {t.backToConfigButton}
                    </button>
                </div>
            )}
        </div>
        <ContactSection></ContactSection>
        <CommentSection
          threadId={'wimhof-comments-111'}
        ></CommentSection>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600 mt-auto">
        {t.footerText(new Date().getFullYear())}
      </footer>
    </div>
  );
};

export default WimHofBreathing;